import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Building2,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Search,
  Upload,
  FileSpreadsheet,
  Eye,
  Download,
  Shield,
  Check,
  Sparkles,
  Brain,
  FileText,
  Globe,
  Home,
  Heart,
  Layers,
  CheckSquare,
  Square,
  X,
  RefreshCw,
  CheckCircle,
  ArrowRight,
  AlertCircle
} from "lucide-react";

// Categorías de aseguradoras
const CATEGORIAS_ASEGURADORA = [
  { value: "todas", label: "Todas", icon: Layers },
  { value: "internacional", label: "Internacionales", icon: Globe },
  { value: "local", label: "Locales/ARS", icon: Home },
  { value: "vida", label: "Vida", icon: Heart },
  { value: "multi_ramo", label: "Multi-Ramo", icon: Building2 }
];

// Categorías de planes
const CATEGORIAS_PLAN = [
  { value: "todas", label: "Todos", icon: Layers },
  { value: "salud_internacional", label: "Salud Internacional", icon: Globe },
  { value: "salud_local", label: "Salud Local", icon: Home },
  { value: "vida", label: "Vida", icon: Heart },
  { value: "accidentes", label: "Accidentes", icon: Shield },
  { value: "mixto", label: "Mixto", icon: Layers }
];

export default function AseguradorasPage() {
  const [aseguradoras, setAseguradoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [editingAseguradora, setEditingAseguradora] = useState(null);
  const [selectedAseguradora, setSelectedAseguradora] = useState(null);
  const [saving, setSaving] = useState(false);
  const [extractingAI, setExtractingAI] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoriaFilter, setCategoriaFilter] = useState("todas");
  const aiFileInputRef = useRef(null);
  
  // Multi-selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [deletingMultiple, setDeletingMultiple] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  
  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  // Bulk import state
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkStep, setBulkStep] = useState(1); // 1: upload, 2: mapping, 3: result
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [asegMapping, setAsegMapping] = useState({});
  const [planesMapping, setPlanesMapping] = useState({});
  const [bulkResult, setBulkResult] = useState(null);
  const bulkFileInputRef = useRef(null);
  
  // Available fields for mapping
  const aseguradoraFields = [
    { value: "name", label: "Nombre" },
    { value: "description", label: "Descripción" },
    { value: "contact_email", label: "Email" },
    { value: "contact_phone", label: "Teléfono" },
    { value: "website", label: "Sitio Web" },
    { value: "categoria", label: "Categoría" }
  ];
  
  const planFields = [
    { value: "aseguradora_name", label: "Nombre Aseguradora" },
    { value: "name", label: "Nombre Plan" },
    { value: "description", label: "Descripción" },
    { value: "categoria", label: "Categoría" },
    { value: "tipo_plan", label: "Tipo Plan" },
    { value: "vigencia", label: "Vigencia" },
    { value: "moneda", label: "Moneda" },
    { value: "cobertura_hospitalizacion", label: "Cobertura Hospitalización" },
    { value: "cobertura_cirugia", label: "Cobertura Cirugía" },
    { value: "cobertura_maternidad", label: "Cobertura Maternidad" },
    { value: "cobertura_emergencia", label: "Cobertura Emergencia" },
    { value: "cobertura_medicamentos", label: "Cobertura Medicamentos" },
    { value: "suma_asegurada_maxima", label: "Suma Asegurada Máxima" },
    { value: "limite_anual", label: "Límite Anual" },
    { value: "copago_consulta", label: "Copago Consulta" },
    { value: "red_medica", label: "Red Médica" },
    { value: "beneficios_adicionales", label: "Beneficios Adicionales" }
  ];
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    logo_url: "",
    contact_email: "",
    contact_phone: "",
    website: "",
    categoria: "multi_ramo"
  });
  const [planFormData, setPlanFormData] = useState({
    name: "",
    description: "",
    categoria: "salud_internacional",
    tipo_plan: "",
    vigencia: "",
    moneda: "USD",
    // Coberturas principales
    cobertura_vida: "",
    cobertura_accidentes: "",
    cobertura_hospitalizacion: "",
    cobertura_cirugia: "",
    cobertura_maternidad: "",
    cobertura_dental: "",
    cobertura_vision: "",
    cobertura_medicamentos: "",
    cobertura_ambulancia: "",
    cobertura_emergencia: "",
    cobertura_rehabilitacion: "",
    cobertura_enfermedades_graves: "",
    // Límites
    suma_asegurada_maxima: "",
    limite_anual: "",
    limite_por_evento: "",
    limite_hospitalizacion_diario: "",
    limite_cirugia: "",
    limite_maternidad: "",
    // Deducibles y copagos
    deducible_general: "",
    deducible_hospitalizacion: "",
    deducible_emergencia: "",
    copago_consulta: "",
    copago_medicamentos: "",
    copago_hospitalizacion: "",
    // Períodos de espera
    espera_general: "",
    espera_maternidad: "",
    espera_preexistencias: "",
    espera_enfermedades_graves: "",
    // Otros
    exclusiones: "",
    prima_mensual: "",
    prima_anual: "",
    prima_por_edad: "",
    red_medica: "",
    hospitales_principales: "",
    cobertura_internacional: "",
    edad_minima: "",
    edad_maxima: "",
    condiciones_renovacion: "",
    beneficios_adicionales: "",
    notas_importantes: "",
    // Legacy fields
    coverages: "",
    limits: "",
    deductibles: "",
    premium_range: "",
    file: null
  });
  const { getAuthHeaders } = useAuth();

  const fetchAseguradoras = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/aseguradoras`, getAuthHeaders());
      setAseguradoras(res.data);
    } catch (err) {
      console.error("Error:", err);
      setAseguradoras([]);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Verificar estado de sincronización
  const checkSyncStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/aseguradoras/sincronizar/estado`, getAuthHeaders());
      setSyncStatus(res.data);
    } catch (err) {
      console.error("Error checking sync status:", err);
    }
  }, [getAuthHeaders]);

  // Sincronizar aseguradoras faltantes
  const handleSyncAseguradoras = async () => {
    try {
      setSyncing(true);
      const res = await axios.post(`${API}/aseguradoras/sincronizar`, {}, getAuthHeaders());
      toast.success(`${res.data.aseguradoras_creadas} aseguradoras y ${res.data.planes_creados} planes sincronizados`);
      fetchAseguradoras();
      checkSyncStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  // Bulk Import Functions
  const openBulkDialog = () => {
    setBulkStep(1);
    setBulkPreview(null);
    setAsegMapping({});
    setPlanesMapping({});
    setBulkResult(null);
    setShowBulkDialog(true);
  };

  const handleBulkFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBulkLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/aseguradoras/bulk-import/preview`, formData, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setBulkPreview(res.data);
      setAsegMapping(res.data.aseguradoras?.column_mapping || {});
      setPlanesMapping(res.data.planes?.column_mapping || {});
      setBulkStep(2);
      toast.success(`${res.data.aseguradoras?.total_rows || 0} aseguradoras y ${res.data.planes?.total_rows || 0} planes encontrados. La IA ha mapeado los campos automáticamente.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar el archivo");
    } finally {
      setBulkLoading(false);
      if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
    }
  };

  const updateAsegMapping = (excelCol, field) => {
    setAsegMapping(prev => {
      const newMapping = { ...prev };
      if (field) {
        newMapping[excelCol] = field;
      } else {
        delete newMapping[excelCol];
      }
      return newMapping;
    });
  };

  const updatePlanesMapping = (excelCol, field) => {
    setPlanesMapping(prev => {
      const newMapping = { ...prev };
      if (field) {
        newMapping[excelCol] = field;
      } else {
        delete newMapping[excelCol];
      }
      return newMapping;
    });
  };

  const executeBulkImport = async () => {
    if (!bulkPreview) return;

    // Validate name mapping exists for aseguradoras
    const hasNameMapping = Object.values(asegMapping).includes('name');
    if (!hasNameMapping) {
      toast.error("Debe mapear una columna al campo 'Nombre' de aseguradoras");
      return;
    }

    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/aseguradoras/bulk-import/execute`, {
        aseguradora_mapping: asegMapping,
        planes_mapping: planesMapping,
        default_categoria_aseguradora: bulkPreview.default_categoria_aseguradora || "local",
        default_categoria_plan: bulkPreview.default_categoria_plan || "salud_local",
        aseguradoras_rows: bulkPreview.aseguradoras?.all_data || [],
        planes_rows: bulkPreview.planes?.all_data || []
      }, getAuthHeaders());

      setBulkResult(res.data);
      setBulkStep(3);
      
      if (res.data.aseguradoras_created > 0 || res.data.planes_created > 0) {
        toast.success(`${res.data.aseguradoras_created} aseguradoras y ${res.data.planes_created} planes importados`);
        fetchAseguradoras();
        checkSyncStatus();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar");
    } finally {
      setBulkLoading(false);
    }
  };

  useEffect(() => {
    fetchAseguradoras();
    checkSyncStatus();
  }, [fetchAseguradoras, checkSyncStatus]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      logo_url: "",
      contact_email: "",
      contact_phone: "",
      website: "",
      categoria: "multi_ramo"
    });
    setEditingAseguradora(null);
  };

  const resetPlanForm = () => {
    setPlanFormData({
      name: "",
      description: "",
      categoria: "salud_internacional",
      tipo_plan: "",
      vigencia: "",
      moneda: "USD",
      cobertura_vida: "",
      cobertura_accidentes: "",
      cobertura_hospitalizacion: "",
      cobertura_cirugia: "",
      cobertura_maternidad: "",
      cobertura_dental: "",
      cobertura_vision: "",
      cobertura_medicamentos: "",
      cobertura_ambulancia: "",
      cobertura_emergencia: "",
      cobertura_rehabilitacion: "",
      cobertura_enfermedades_graves: "",
      suma_asegurada_maxima: "",
      limite_anual: "",
      limite_por_evento: "",
      limite_hospitalizacion_diario: "",
      limite_cirugia: "",
      limite_maternidad: "",
      deducible_general: "",
      deducible_hospitalizacion: "",
      deducible_emergencia: "",
      copago_consulta: "",
      copago_medicamentos: "",
      copago_hospitalizacion: "",
      espera_general: "",
      espera_maternidad: "",
      espera_preexistencias: "",
      espera_enfermedades_graves: "",
      exclusiones: "",
      prima_mensual: "",
      prima_anual: "",
      prima_por_edad: "",
      red_medica: "",
      hospitales_principales: "",
      cobertura_internacional: "",
      edad_minima: "",
      edad_maxima: "",
      condiciones_renovacion: "",
      beneficios_adicionales: "",
      notas_importantes: "",
      coverages: "",
      limits: "",
      deductibles: "",
      premium_range: "",
      file: null
    });
  };

  // Multi-selection functions
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedItems(new Set());
  };

  const toggleItemSelection = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const selectAllItems = () => {
    const allIds = new Set(filteredAseguradoras.map(a => a.id));
    setSelectedItems(allIds);
  };

  const deselectAllItems = () => {
    setSelectedItems(new Set());
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteSelected = async () => {
    setDeletingMultiple(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of selectedItems) {
      try {
        await axios.delete(`${API}/aseguradoras/${id}`, getAuthHeaders());
        successCount++;
      } catch (error) {
        console.error(`Error deleting aseguradora ${id}:`, error);
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} aseguradora(s) eliminada(s) exitosamente`);
      fetchAseguradoras();
    }
    if (errorCount > 0) {
      toast.error(`No se pudieron eliminar ${errorCount} aseguradora(s)`);
    }

    setSelectedItems(new Set());
    setIsSelectionMode(false);
    setDeletingMultiple(false);
    setShowDeleteConfirmDialog(false);
  };

  const handleExtractFromDocument = async (file) => {
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo se permiten archivos PDF o imágenes (JPG, PNG)");
      return;
    }

    setExtractingAI(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/planes-cobertura/extract-from-document`,
        formDataUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success && response.data.extracted_data) {
        const data = response.data.extracted_data;
        setPlanFormData(prev => ({
          ...prev,
          name: data.name || prev.name,
          description: data.description || prev.description,
          tipo_plan: data.tipo_plan || prev.tipo_plan,
          vigencia: data.vigencia || prev.vigencia,
          moneda: data.moneda || prev.moneda,
          cobertura_vida: data.cobertura_vida || prev.cobertura_vida,
          cobertura_accidentes: data.cobertura_accidentes || prev.cobertura_accidentes,
          cobertura_hospitalizacion: data.cobertura_hospitalizacion || prev.cobertura_hospitalizacion,
          cobertura_cirugia: data.cobertura_cirugia || prev.cobertura_cirugia,
          cobertura_maternidad: data.cobertura_maternidad || prev.cobertura_maternidad,
          cobertura_dental: data.cobertura_dental || prev.cobertura_dental,
          cobertura_vision: data.cobertura_vision || prev.cobertura_vision,
          cobertura_medicamentos: data.cobertura_medicamentos || prev.cobertura_medicamentos,
          cobertura_ambulancia: data.cobertura_ambulancia || prev.cobertura_ambulancia,
          cobertura_emergencia: data.cobertura_emergencia || prev.cobertura_emergencia,
          cobertura_rehabilitacion: data.cobertura_rehabilitacion || prev.cobertura_rehabilitacion,
          cobertura_enfermedades_graves: data.cobertura_enfermedades_graves || prev.cobertura_enfermedades_graves,
          suma_asegurada_maxima: data.suma_asegurada_maxima || prev.suma_asegurada_maxima,
          limite_anual: data.limite_anual || prev.limite_anual,
          limite_por_evento: data.limite_por_evento || prev.limite_por_evento,
          limite_hospitalizacion_diario: data.limite_hospitalizacion_diario || prev.limite_hospitalizacion_diario,
          limite_cirugia: data.limite_cirugia || prev.limite_cirugia,
          limite_maternidad: data.limite_maternidad || prev.limite_maternidad,
          deducible_general: data.deducible_general || prev.deducible_general,
          deducible_hospitalizacion: data.deducible_hospitalizacion || prev.deducible_hospitalizacion,
          deducible_emergencia: data.deducible_emergencia || prev.deducible_emergencia,
          copago_consulta: data.copago_consulta || prev.copago_consulta,
          copago_medicamentos: data.copago_medicamentos || prev.copago_medicamentos,
          copago_hospitalizacion: data.copago_hospitalizacion || prev.copago_hospitalizacion,
          espera_general: data.espera_general || prev.espera_general,
          espera_maternidad: data.espera_maternidad || prev.espera_maternidad,
          espera_preexistencias: data.espera_preexistencias || prev.espera_preexistencias,
          espera_enfermedades_graves: data.espera_enfermedades_graves || prev.espera_enfermedades_graves,
          exclusiones: data.exclusiones || prev.exclusiones,
          prima_mensual: data.prima_mensual || prev.prima_mensual,
          prima_anual: data.prima_anual || prev.prima_anual,
          prima_por_edad: data.prima_por_edad || prev.prima_por_edad,
          red_medica: data.red_medica || prev.red_medica,
          hospitales_principales: data.hospitales_principales || prev.hospitales_principales,
          cobertura_internacional: data.cobertura_internacional || prev.cobertura_internacional,
          edad_minima: data.edad_minima || prev.edad_minima,
          edad_maxima: data.edad_maxima || prev.edad_maxima,
          condiciones_renovacion: data.condiciones_renovacion || prev.condiciones_renovacion,
          beneficios_adicionales: data.beneficios_adicionales || prev.beneficios_adicionales,
          notas_importantes: data.notas_importantes || prev.notas_importantes,
        }));
        toast.success("Datos extraídos correctamente con IA");
      } else {
        toast.error(response.data.message || "Error al extraer datos");
      }
    } catch (err) {
      console.error("Error extracting:", err);
      toast.error(err.response?.data?.detail || "Error al procesar documento");
    } finally {
      setExtractingAI(false);
    }
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (aseguradora) => {
    setEditingAseguradora(aseguradora);
    setFormData({
      name: aseguradora.name,
      description: aseguradora.description || "",
      logo_url: aseguradora.logo_url || "",
      contact_email: aseguradora.contact_email || "",
      contact_phone: aseguradora.contact_phone || "",
      website: aseguradora.website || "",
      categoria: aseguradora.categoria || "multi_ramo"
    });
    setShowDialog(true);
  };

  const openPlanDialog = (aseguradora) => {
    setSelectedAseguradora(aseguradora);
    resetPlanForm();
    setShowPlanDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    setSaving(true);
    try {
      if (editingAseguradora) {
        await axios.put(`${API}/aseguradoras/${editingAseguradora.id}`, formData, getAuthHeaders());
        toast.success("Aseguradora actualizada");
      } else {
        await axios.post(`${API}/aseguradoras`, formData, getAuthHeaders());
        toast.success("Aseguradora creada");
      }
      setShowDialog(false);
      resetForm();
      fetchAseguradoras();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlan = async () => {
    if (!planFormData.name.trim()) {
      toast.error("El nombre del plan es requerido");
      return;
    }

    setSaving(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("aseguradora_id", selectedAseguradora.id);
      formDataToSend.append("name", planFormData.name);
      formDataToSend.append("description", planFormData.description);
      formDataToSend.append("categoria", planFormData.categoria);
      formDataToSend.append("tipo_plan", planFormData.tipo_plan);
      formDataToSend.append("vigencia", planFormData.vigencia);
      formDataToSend.append("moneda", planFormData.moneda);
      // Coberturas
      formDataToSend.append("cobertura_vida", planFormData.cobertura_vida);
      formDataToSend.append("cobertura_accidentes", planFormData.cobertura_accidentes);
      formDataToSend.append("cobertura_hospitalizacion", planFormData.cobertura_hospitalizacion);
      formDataToSend.append("cobertura_cirugia", planFormData.cobertura_cirugia);
      formDataToSend.append("cobertura_maternidad", planFormData.cobertura_maternidad);
      formDataToSend.append("cobertura_dental", planFormData.cobertura_dental);
      formDataToSend.append("cobertura_vision", planFormData.cobertura_vision);
      formDataToSend.append("cobertura_medicamentos", planFormData.cobertura_medicamentos);
      formDataToSend.append("cobertura_ambulancia", planFormData.cobertura_ambulancia);
      formDataToSend.append("cobertura_emergencia", planFormData.cobertura_emergencia);
      formDataToSend.append("cobertura_rehabilitacion", planFormData.cobertura_rehabilitacion);
      formDataToSend.append("cobertura_enfermedades_graves", planFormData.cobertura_enfermedades_graves);
      // Límites
      formDataToSend.append("suma_asegurada_maxima", planFormData.suma_asegurada_maxima);
      formDataToSend.append("limite_anual", planFormData.limite_anual);
      formDataToSend.append("limite_por_evento", planFormData.limite_por_evento);
      formDataToSend.append("limite_hospitalizacion_diario", planFormData.limite_hospitalizacion_diario);
      formDataToSend.append("limite_cirugia", planFormData.limite_cirugia);
      formDataToSend.append("limite_maternidad", planFormData.limite_maternidad);
      // Deducibles
      formDataToSend.append("deducible_general", planFormData.deducible_general);
      formDataToSend.append("deducible_hospitalizacion", planFormData.deducible_hospitalizacion);
      formDataToSend.append("deducible_emergencia", planFormData.deducible_emergencia);
      formDataToSend.append("copago_consulta", planFormData.copago_consulta);
      formDataToSend.append("copago_medicamentos", planFormData.copago_medicamentos);
      formDataToSend.append("copago_hospitalizacion", planFormData.copago_hospitalizacion);
      // Esperas
      formDataToSend.append("espera_general", planFormData.espera_general);
      formDataToSend.append("espera_maternidad", planFormData.espera_maternidad);
      formDataToSend.append("espera_preexistencias", planFormData.espera_preexistencias);
      formDataToSend.append("espera_enfermedades_graves", planFormData.espera_enfermedades_graves);
      // Otros
      formDataToSend.append("exclusiones", planFormData.exclusiones);
      formDataToSend.append("prima_mensual", planFormData.prima_mensual);
      formDataToSend.append("prima_anual", planFormData.prima_anual);
      formDataToSend.append("prima_por_edad", planFormData.prima_por_edad);
      formDataToSend.append("red_medica", planFormData.red_medica);
      formDataToSend.append("hospitales_principales", planFormData.hospitales_principales);
      formDataToSend.append("cobertura_internacional", planFormData.cobertura_internacional);
      formDataToSend.append("edad_minima", planFormData.edad_minima);
      formDataToSend.append("edad_maxima", planFormData.edad_maxima);
      formDataToSend.append("condiciones_renovacion", planFormData.condiciones_renovacion);
      formDataToSend.append("beneficios_adicionales", planFormData.beneficios_adicionales);
      formDataToSend.append("notas_importantes", planFormData.notas_importantes);
      // Legacy
      formDataToSend.append("coverages", planFormData.coverages);
      formDataToSend.append("limits", planFormData.limits);
      formDataToSend.append("deductibles", planFormData.deductibles);
      formDataToSend.append("premium_range", planFormData.premium_range);
      
      if (planFormData.file) {
        formDataToSend.append("file", planFormData.file);
      }

      await axios.post(`${API}/planes-cobertura`, formDataToSend, {
        ...getAuthHeaders(),
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success("Plan de cobertura agregado");
      setShowPlanDialog(false);
      resetPlanForm();
      fetchAseguradoras();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el plan");
    } finally {
      setSaving(false);
    }
  };

  const deleteAseguradora = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta aseguradora?")) return;
    try {
      await axios.delete(`${API}/aseguradoras/${id}`, getAuthHeaders());
      toast.success("Aseguradora eliminada");
      setAseguradoras(aseguradoras.filter(a => a.id !== id));
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const filteredAseguradoras = aseguradoras.filter(a => {
    const matchesSearch = a.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategoria = categoriaFilter === "todas" || a.categoria === categoriaFilter;
    return matchesSearch && matchesCategoria;
  });

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="aseguradoras-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Aseguradoras
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Gestiona las aseguradoras y sus cuadros de cobertura
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Multi-selection controls */}
            {isSelectionMode ? (
              <>
                <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-lg">
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    {selectedItems.size} seleccionados
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllItems}
                  className="text-slate-600 dark:text-slate-300"
                  data-testid="select-all-button"
                >
                  <CheckSquare className="w-4 h-4 mr-1" />
                  Todos
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deselectAllItems}
                  className="text-slate-600 dark:text-slate-300"
                  data-testid="deselect-all-button"
                >
                  <Square className="w-4 h-4 mr-1" />
                  Ninguno
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={selectedItems.size === 0 || deletingMultiple}
                  data-testid="delete-selected-button"
                >
                  {deletingMultiple ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Eliminar ({selectedItems.size})
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectionMode}
                  data-testid="cancel-selection-button"
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline"
                  onClick={toggleSelectionMode}
                  className="text-slate-600 dark:text-slate-300"
                  data-testid="enable-selection-button"
                >
                  <CheckSquare className="w-4 h-4 mr-2" />
                  Seleccionar
                </Button>
                
                {/* Botón de sincronización */}
                {syncStatus && syncStatus.faltantes > 0 && (
                  <Button 
                    variant="outline"
                    onClick={handleSyncAseguradoras}
                    disabled={syncing}
                    className="border-orange-300 text-orange-600 hover:bg-orange-50 dark:border-orange-600 dark:text-orange-400 dark:hover:bg-orange-900/20"
                    data-testid="sync-aseguradoras-button"
                  >
                    {syncing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Sincronizar ({syncStatus.faltantes})
                  </Button>
                )}
                
                {/* Badge de sincronizado */}
                {syncStatus && syncStatus.faltantes === 0 && aseguradoras.length > 0 && (
                  <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200 py-2 px-3 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Sincronizado
                  </Badge>
                )}
                
                {/* Botón Importar Excel */}
                <Button 
                  variant="outline"
                  onClick={openBulkDialog}
                  className="border-[#0C314C] text-[#0C314C] hover:bg-[#0C314C]/10"
                  data-testid="bulk-import-aseguradoras-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importar Excel
                </Button>
                
                <Button 
                  onClick={openNewDialog}
                  className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                  data-testid="new-aseguradora-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Aseguradora
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="border-slate-200 dark:border-slate-700 shadow-sm mb-6 dark:bg-slate-800">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Buscar aseguradora..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                  data-testid="search-aseguradoras"
                />
              </div>
              
              {/* Category Filters */}
              <div className="flex gap-2 flex-wrap">
                {CATEGORIAS_ASEGURADORA.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <Button
                      key={cat.value}
                      variant={categoriaFilter === cat.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCategoriaFilter(cat.value)}
                      className={categoriaFilter === cat.value 
                        ? "bg-[#0C314C] hover:bg-[#2a4d7a] text-white" 
                        : "border-slate-200 dark:border-slate-600"
                      }
                      data-testid={`filter-${cat.value}`}
                    >
                      <Icon className="w-4 h-4 mr-1" />
                      {cat.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Aseguradoras Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredAseguradoras.length === 0 ? (
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
            <CardContent className="text-center py-12">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                No hay aseguradoras registradas
              </p>
              <Button onClick={openNewDialog} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Agregar primera aseguradora
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAseguradoras.map((aseguradora) => (
              <Card 
                key={aseguradora.id} 
                className={`border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800 hover:shadow-md transition-shadow relative ${
                  isSelectionMode && selectedItems.has(aseguradora.id) ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
                data-testid={`aseguradora-card-${aseguradora.id}`}
              >
                {/* Selection checkbox */}
                {isSelectionMode && (
                  <div className="absolute top-3 left-3 z-10">
                    <Checkbox
                      checked={selectedItems.has(aseguradora.id)}
                      onCheckedChange={() => toggleItemSelection(aseguradora.id)}
                      className="w-5 h-5 bg-white dark:bg-slate-700 border-2"
                      data-testid={`checkbox-${aseguradora.id}`}
                    />
                  </div>
                )}
                <CardHeader className={`pb-3 ${isSelectionMode ? 'pl-12' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {aseguradora.logo_url ? (
                        <img src={aseguradora.logo_url} alt={aseguradora.name} className="w-12 h-12 object-contain rounded" />
                      ) : (
                        <div className="w-12 h-12 bg-[#0C314C] rounded-lg flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-white" />
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-base font-semibold text-slate-900 dark:text-white">
                          {aseguradora.name}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            aseguradora.categoria === 'internacional' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' :
                            aseguradora.categoria === 'local' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                            aseguradora.categoria === 'vida' ? 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300' :
                            'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                          }`}>
                            {aseguradora.categoria === 'internacional' ? 'Internacional' :
                             aseguradora.categoria === 'local' ? 'Local/ARS' :
                             aseguradora.categoria === 'vida' ? 'Vida' : 'Multi-Ramo'}
                          </span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {aseguradora.planes?.length || 0} planes
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {aseguradora.description && (
                    <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 line-clamp-2">
                      {aseguradora.description}
                    </p>
                  )}
                  
                  {/* Plans List */}
                  {aseguradora.planes && aseguradora.planes.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {aseguradora.planes.slice(0, 3).map((plan) => (
                        <div key={plan.id} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700 rounded px-2 py-1">
                          <Shield className="w-3 h-3 text-[#45E69A]" />
                          <span className="truncate">{plan.name}</span>
                        </div>
                      ))}
                      {aseguradora.planes.length > 3 && (
                        <p className="text-xs text-slate-400">+{aseguradora.planes.length - 3} más</p>
                      )}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-700">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openPlanDialog(aseguradora)}
                      className="flex-1 text-xs"
                      data-testid={`add-plan-${aseguradora.id}`}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Agregar Plan
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(aseguradora)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteAseguradora(aseguradora.id)}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Aseguradora Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope']">
                {editingAseguradora ? "Editar Aseguradora" : "Nueva Aseguradora"}
              </DialogTitle>
              <DialogDescription>
                {editingAseguradora ? "Actualiza los datos de la aseguradora" : "Registra una nueva aseguradora"}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ej: Seguros Patria"
                  data-testid="aseguradora-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Breve descripción de la aseguradora"
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="logo_url">URL del Logo</Label>
                <Input
                  id="logo_url"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                  placeholder="https://..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                    placeholder="contacto@..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Teléfono</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                    placeholder="+1 809..."
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="categoria">Categoría</Label>
                <select
                  id="categoria"
                  value={formData.categoria}
                  onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white text-sm"
                >
                  <option value="internacional">Seguros Internacionales</option>
                  <option value="local">Seguros Locales/ARS</option>
                  <option value="vida">Seguros de Vida</option>
                  <option value="multi_ramo">Multi-Ramo</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="website">Sitio Web</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  placeholder="https://www..."
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                data-testid="save-aseguradora-button"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Plan Dialog */}
        <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope']">
                Agregar Plan de Cobertura
              </DialogTitle>
              <DialogDescription>
                {selectedAseguradora?.name} - Cuadro de coberturas y beneficios
              </DialogDescription>
            </DialogHeader>
            
            {/* AI Extraction Button */}
            <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Brain className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-slate-800 dark:text-white">Extracción con IA</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Sube un cuadro de coberturas (PDF o imagen) y la IA completará automáticamente los campos
                </p>
              </div>
              <div>
                <input
                  type="file"
                  ref={aiFileInputRef}
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleExtractFromDocument(e.target.files[0])}
                  className="hidden"
                />
                <Button
                  onClick={() => aiFileInputRef.current?.click()}
                  disabled={extractingAI}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  {extractingAI ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Extrayendo...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Cargar y Extraer
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            <ScrollArea className="h-[50vh] pr-4">
              <div className="space-y-6 py-4">
                {/* Información básica */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 dark:text-white border-b pb-2">Información Básica</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nombre del Plan *</Label>
                      <Input
                        value={planFormData.name}
                        onChange={(e) => setPlanFormData({...planFormData, name: e.target.value})}
                        placeholder="Ej: Plan Premium Vida"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Categoría del Plan</Label>
                      <select
                        value={planFormData.categoria}
                        onChange={(e) => setPlanFormData({...planFormData, categoria: e.target.value})}
                        className="w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white text-sm"
                      >
                        <option value="salud_internacional">Salud Internacional</option>
                        <option value="salud_local">Salud Local</option>
                        <option value="vida">Vida</option>
                        <option value="accidentes">Accidentes</option>
                        <option value="mixto">Mixto</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Tipo de Plan</Label>
                      <Input
                        value={planFormData.tipo_plan}
                        onChange={(e) => setPlanFormData({...planFormData, tipo_plan: e.target.value})}
                        placeholder="Individual/Familiar/Colectivo"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Vigencia</Label>
                      <Input
                        value={planFormData.vigencia}
                        onChange={(e) => setPlanFormData({...planFormData, vigencia: e.target.value})}
                        placeholder="Ej: 1 año"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Descripción</Label>
                    <Textarea
                      value={planFormData.description}
                      onChange={(e) => setPlanFormData({...planFormData, description: e.target.value})}
                      placeholder="Descripción del plan"
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Moneda</Label>
                      <Input
                        value={planFormData.moneda}
                        onChange={(e) => setPlanFormData({...planFormData, moneda: e.target.value})}
                        placeholder="USD/RD$/EUR"
                      />
                    </div>
                  </div>
                </div>

                {/* Coberturas principales */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 dark:text-white border-b pb-2">Coberturas Principales</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Vida</Label>
                      <Input value={planFormData.cobertura_vida} onChange={(e) => setPlanFormData({...planFormData, cobertura_vida: e.target.value})} placeholder="Cobertura de vida" />
                    </div>
                    <div className="space-y-2">
                      <Label>Accidentes</Label>
                      <Input value={planFormData.cobertura_accidentes} onChange={(e) => setPlanFormData({...planFormData, cobertura_accidentes: e.target.value})} placeholder="Cobertura accidentes" />
                    </div>
                    <div className="space-y-2">
                      <Label>Hospitalización</Label>
                      <Input value={planFormData.cobertura_hospitalizacion} onChange={(e) => setPlanFormData({...planFormData, cobertura_hospitalizacion: e.target.value})} placeholder="Cobertura hospitalización" />
                    </div>
                    <div className="space-y-2">
                      <Label>Cirugía</Label>
                      <Input value={planFormData.cobertura_cirugia} onChange={(e) => setPlanFormData({...planFormData, cobertura_cirugia: e.target.value})} placeholder="Cobertura cirugía" />
                    </div>
                    <div className="space-y-2">
                      <Label>Maternidad</Label>
                      <Input value={planFormData.cobertura_maternidad} onChange={(e) => setPlanFormData({...planFormData, cobertura_maternidad: e.target.value})} placeholder="Cobertura maternidad" />
                    </div>
                    <div className="space-y-2">
                      <Label>Dental</Label>
                      <Input value={planFormData.cobertura_dental} onChange={(e) => setPlanFormData({...planFormData, cobertura_dental: e.target.value})} placeholder="Cobertura dental" />
                    </div>
                    <div className="space-y-2">
                      <Label>Visión</Label>
                      <Input value={planFormData.cobertura_vision} onChange={(e) => setPlanFormData({...planFormData, cobertura_vision: e.target.value})} placeholder="Cobertura visión" />
                    </div>
                    <div className="space-y-2">
                      <Label>Medicamentos</Label>
                      <Input value={planFormData.cobertura_medicamentos} onChange={(e) => setPlanFormData({...planFormData, cobertura_medicamentos: e.target.value})} placeholder="Cobertura medicamentos" />
                    </div>
                    <div className="space-y-2">
                      <Label>Emergencia</Label>
                      <Input value={planFormData.cobertura_emergencia} onChange={(e) => setPlanFormData({...planFormData, cobertura_emergencia: e.target.value})} placeholder="Cobertura emergencia" />
                    </div>
                    <div className="space-y-2">
                      <Label>Enfermedades Graves</Label>
                      <Input value={planFormData.cobertura_enfermedades_graves} onChange={(e) => setPlanFormData({...planFormData, cobertura_enfermedades_graves: e.target.value})} placeholder="Enfermedades graves" />
                    </div>
                  </div>
                </div>

                {/* Límites y Sumas Aseguradas */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 dark:text-white border-b pb-2">Límites y Sumas Aseguradas</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Suma Asegurada Máxima</Label>
                      <Input value={planFormData.suma_asegurada_maxima} onChange={(e) => setPlanFormData({...planFormData, suma_asegurada_maxima: e.target.value})} placeholder="Ej: $500,000" />
                    </div>
                    <div className="space-y-2">
                      <Label>Límite Anual</Label>
                      <Input value={planFormData.limite_anual} onChange={(e) => setPlanFormData({...planFormData, limite_anual: e.target.value})} placeholder="Límite anual" />
                    </div>
                    <div className="space-y-2">
                      <Label>Límite por Evento</Label>
                      <Input value={planFormData.limite_por_evento} onChange={(e) => setPlanFormData({...planFormData, limite_por_evento: e.target.value})} placeholder="Límite por evento" />
                    </div>
                    <div className="space-y-2">
                      <Label>Límite Hospitalización Diario</Label>
                      <Input value={planFormData.limite_hospitalizacion_diario} onChange={(e) => setPlanFormData({...planFormData, limite_hospitalizacion_diario: e.target.value})} placeholder="Límite diario" />
                    </div>
                  </div>
                </div>

                {/* Deducibles y Copagos */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 dark:text-white border-b pb-2">Deducibles y Copagos</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Deducible General</Label>
                      <Input value={planFormData.deducible_general} onChange={(e) => setPlanFormData({...planFormData, deducible_general: e.target.value})} placeholder="Deducible general" />
                    </div>
                    <div className="space-y-2">
                      <Label>Deducible Emergencia</Label>
                      <Input value={planFormData.deducible_emergencia} onChange={(e) => setPlanFormData({...planFormData, deducible_emergencia: e.target.value})} placeholder="Deducible emergencia" />
                    </div>
                    <div className="space-y-2">
                      <Label>Copago Consulta</Label>
                      <Input value={planFormData.copago_consulta} onChange={(e) => setPlanFormData({...planFormData, copago_consulta: e.target.value})} placeholder="Copago consulta" />
                    </div>
                    <div className="space-y-2">
                      <Label>Copago Medicamentos</Label>
                      <Input value={planFormData.copago_medicamentos} onChange={(e) => setPlanFormData({...planFormData, copago_medicamentos: e.target.value})} placeholder="Copago medicamentos" />
                    </div>
                  </div>
                </div>

                {/* Períodos de Espera */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 dark:text-white border-b pb-2">Períodos de Espera</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Espera General</Label>
                      <Input value={planFormData.espera_general} onChange={(e) => setPlanFormData({...planFormData, espera_general: e.target.value})} placeholder="Ej: 30 días" />
                    </div>
                    <div className="space-y-2">
                      <Label>Espera Maternidad</Label>
                      <Input value={planFormData.espera_maternidad} onChange={(e) => setPlanFormData({...planFormData, espera_maternidad: e.target.value})} placeholder="Ej: 10 meses" />
                    </div>
                    <div className="space-y-2">
                      <Label>Espera Preexistencias</Label>
                      <Input value={planFormData.espera_preexistencias} onChange={(e) => setPlanFormData({...planFormData, espera_preexistencias: e.target.value})} placeholder="Espera preexistencias" />
                    </div>
                    <div className="space-y-2">
                      <Label>Espera Enf. Graves</Label>
                      <Input value={planFormData.espera_enfermedades_graves} onChange={(e) => setPlanFormData({...planFormData, espera_enfermedades_graves: e.target.value})} placeholder="Espera enf. graves" />
                    </div>
                  </div>
                </div>

                {/* Primas */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 dark:text-white border-b pb-2">Primas</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prima Mensual</Label>
                      <Input value={planFormData.prima_mensual} onChange={(e) => setPlanFormData({...planFormData, prima_mensual: e.target.value})} placeholder="Prima mensual" />
                    </div>
                    <div className="space-y-2">
                      <Label>Prima Anual</Label>
                      <Input value={planFormData.prima_anual} onChange={(e) => setPlanFormData({...planFormData, prima_anual: e.target.value})} placeholder="Prima anual" />
                    </div>
                  </div>
                </div>

                {/* Red Médica y Otros */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 dark:text-white border-b pb-2">Red Médica y Condiciones</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Red Médica</Label>
                      <Input value={planFormData.red_medica} onChange={(e) => setPlanFormData({...planFormData, red_medica: e.target.value})} placeholder="Red médica" />
                    </div>
                    <div className="space-y-2">
                      <Label>Cobertura Internacional</Label>
                      <Input value={planFormData.cobertura_internacional} onChange={(e) => setPlanFormData({...planFormData, cobertura_internacional: e.target.value})} placeholder="Cobertura internacional" />
                    </div>
                    <div className="space-y-2">
                      <Label>Edad Mínima</Label>
                      <Input value={planFormData.edad_minima} onChange={(e) => setPlanFormData({...planFormData, edad_minima: e.target.value})} placeholder="Edad mínima" />
                    </div>
                    <div className="space-y-2">
                      <Label>Edad Máxima</Label>
                      <Input value={planFormData.edad_maxima} onChange={(e) => setPlanFormData({...planFormData, edad_maxima: e.target.value})} placeholder="Edad máxima" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Exclusiones</Label>
                    <Textarea value={planFormData.exclusiones} onChange={(e) => setPlanFormData({...planFormData, exclusiones: e.target.value})} placeholder="Exclusiones del plan" rows={2} />
                  </div>
                  <div className="space-y-2">
                    <Label>Beneficios Adicionales</Label>
                    <Textarea value={planFormData.beneficios_adicionales} onChange={(e) => setPlanFormData({...planFormData, beneficios_adicionales: e.target.value})} placeholder="Beneficios adicionales" rows={2} />
                  </div>
                  <div className="space-y-2">
                    <Label>Notas Importantes</Label>
                    <Textarea value={planFormData.notas_importantes} onChange={(e) => setPlanFormData({...planFormData, notas_importantes: e.target.value})} placeholder="Notas importantes" rows={2} />
                  </div>
                </div>

                {/* Archivo */}
                <div className="space-y-2">
                  <Label>Archivo del Cuadro (Excel/PDF/Imagen)</Label>
                  <div className="border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-lg p-4">
                    <input
                      type="file"
                      id="plan_file_upload"
                      accept=".xlsx,.xls,.pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setPlanFormData({...planFormData, file: e.target.files[0]})}
                      className="hidden"
                    />
                    <label htmlFor="plan_file_upload" className="cursor-pointer flex flex-col items-center gap-2">
                      {planFormData.file ? (
                        <>
                          <Check className="w-8 h-8 text-green-500" />
                          <span className="text-sm text-slate-600 dark:text-slate-300">{planFormData.file.name}</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-slate-400" />
                          <span className="text-sm text-slate-500">Haz clic para subir archivo</span>
                          <span className="text-xs text-slate-400">Excel, PDF o Imagen</span>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            </ScrollArea>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPlanDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSavePlan} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                data-testid="save-plan-button"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {saving ? "Guardando..." : "Guardar Plan"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-slate-900 dark:text-white">
                Confirmar eliminación
              </DialogTitle>
              <DialogDescription>
                ¿Estás seguro de que deseas eliminar {selectedItems.size} aseguradora(s)? 
                Esta acción también eliminará todos los planes asociados y no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirmDialog(false)}
                disabled={deletingMultiple}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteSelected}
                disabled={deletingMultiple}
                data-testid="confirm-delete-button"
              >
                {deletingMultiple ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar {selectedItems.size}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Import Dialog */}
        <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
          <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope'] flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#0C314C]" />
                Importar Aseguradoras y Planes desde Excel
              </DialogTitle>
              <DialogDescription>
                {bulkStep === 1 && "Sube un archivo Excel (.xlsx) con los datos de aseguradoras y opcionalmente sus planes"}
                {bulkStep === 2 && "Verifica el mapeo de columnas generado por la IA"}
                {bulkStep === 3 && "Resultado de la importación"}
              </DialogDescription>
            </DialogHeader>

            {/* Progress Steps */}
            <div className="flex items-center justify-center gap-2 py-4 border-b dark:border-slate-700">
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 1 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">1</span>
                Subir
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 2 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">2</span>
                Mapear
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${bulkStep >= 3 ? 'bg-[#0C314C] text-white' : 'bg-slate-200 text-slate-500'}`}>
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">3</span>
                Resultado
              </div>
            </div>

            <div className="flex-1 overflow-auto py-4">
              {/* Step 1: Upload */}
              {bulkStep === 1 && (
                <div className="flex flex-col items-center justify-center py-12">
                  <input
                    ref={bulkFileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleBulkFileUpload}
                    className="hidden"
                  />
                  
                  <div className="w-20 h-20 rounded-full bg-[#0C314C]/10 flex items-center justify-center mb-6">
                    <FileSpreadsheet className="w-10 h-10 text-[#0C314C]" />
                  </div>
                  
                  {bulkLoading ? (
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-[#0C314C] mx-auto mb-4" />
                      <p className="text-slate-600 dark:text-slate-400">Analizando archivo con IA...</p>
                    </div>
                  ) : (
                    <>
                      <Button 
                        onClick={() => bulkFileInputRef.current?.click()}
                        className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white mb-4"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Seleccionar Archivo Excel
                      </Button>
                      <div className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-lg space-y-2">
                        <p>
                          La IA analizará las columnas de tu archivo y las mapeará automáticamente.
                        </p>
                        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-left text-xs">
                          <p className="font-semibold mb-1">Formato sugerido del Excel:</p>
                          <p>• <strong>Hoja 1 (Aseguradoras):</strong> Nombre, Descripción, Email, Teléfono, Web, Categoría</p>
                          <p>• <strong>Hoja 2 (Planes - opcional):</strong> Aseguradora, Nombre Plan, Coberturas, Límites...</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Step 2: Mapping */}
              {bulkStep === 2 && bulkPreview && (
                <ScrollArea className="h-[55vh]">
                  <div className="space-y-6 pr-4">
                    {/* Aseguradoras Mapping */}
                    <div>
                      <h3 className="font-semibold mb-3 text-slate-900 dark:text-white flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-[#0C314C]" />
                        Mapeo de Aseguradoras ({bulkPreview.aseguradoras?.total_rows || 0} filas)
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        {bulkPreview.aseguradoras?.headers?.map(header => (
                          <div key={`aseg-${header}`} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate" title={header}>
                              {header}
                            </span>
                            <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            <Select 
                              value={asegMapping[header] || "_none"} 
                              onValueChange={(value) => updateAsegMapping(header, value === "_none" ? "" : value)}
                            >
                              <SelectTrigger className="w-36 text-xs h-8">
                                <SelectValue placeholder="No mapear" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">No mapear</SelectItem>
                                {aseguradoraFields.map(field => (
                                  <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                      
                      {/* Aseguradoras Preview */}
                      {bulkPreview.aseguradoras?.sample_data?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-slate-500 mb-2">Vista previa de datos:</p>
                          <ScrollArea className="h-32 border rounded-lg dark:border-slate-700">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                                <tr>
                                  {bulkPreview.aseguradoras.headers.slice(0, 4).map(h => (
                                    <th key={h} className="text-left p-2 font-medium text-slate-600 dark:text-slate-300">{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {bulkPreview.aseguradoras.sample_data.slice(0, 5).map((row, idx) => (
                                  <tr key={idx}>
                                    {bulkPreview.aseguradoras.headers.slice(0, 4).map(h => (
                                      <td key={h} className="p-2 text-slate-700 dark:text-slate-300">{row[h] || '-'}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </ScrollArea>
                        </div>
                      )}
                    </div>

                    {/* Planes Mapping (if available) */}
                    {bulkPreview.planes && bulkPreview.planes.headers?.length > 0 && (
                      <div>
                        <h3 className="font-semibold mb-3 text-slate-900 dark:text-white flex items-center gap-2">
                          <Shield className="w-4 h-4 text-[#45E69A]" />
                          Mapeo de Planes ({bulkPreview.planes?.total_rows || 0} filas)
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                          {bulkPreview.planes.headers.map(header => (
                            <div key={`plan-${header}`} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                              <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate" title={header}>
                                {header}
                              </span>
                              <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              <Select 
                                value={planesMapping[header] || "_none"} 
                                onValueChange={(value) => updatePlanesMapping(header, value === "_none" ? "" : value)}
                              >
                                <SelectTrigger className="w-40 text-xs h-8">
                                  <SelectValue placeholder="No mapear" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">No mapear</SelectItem>
                                  {planFields.map(field => (
                                    <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          ))}
                        </div>

                        {/* Planes Preview */}
                        {bulkPreview.planes.sample_data?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-slate-500 mb-2">Vista previa de planes:</p>
                            <ScrollArea className="h-32 border rounded-lg dark:border-slate-700">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                                  <tr>
                                    {bulkPreview.planes.headers.slice(0, 4).map(h => (
                                      <th key={h} className="text-left p-2 font-medium text-slate-600 dark:text-slate-300">{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                  {bulkPreview.planes.sample_data.slice(0, 5).map((row, idx) => (
                                    <tr key={idx}>
                                      {bulkPreview.planes.headers.slice(0, 4).map(h => (
                                        <td key={h} className="p-2 text-slate-700 dark:text-slate-300">{row[h] || '-'}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </ScrollArea>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Defaults */}
                    <div className="flex gap-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <div className="flex-1">
                        <Label className="text-xs text-amber-700 dark:text-amber-300">Categoría aseguradora por defecto</Label>
                        <Select 
                          value={bulkPreview.default_categoria_aseguradora || "local"} 
                          onValueChange={(value) => setBulkPreview({...bulkPreview, default_categoria_aseguradora: value})}
                        >
                          <SelectTrigger className="h-8 text-sm mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="internacional">Internacional</SelectItem>
                            <SelectItem value="local">Local/ARS</SelectItem>
                            <SelectItem value="vida">Vida</SelectItem>
                            <SelectItem value="multi_ramo">Multi-Ramo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {bulkPreview.planes && (
                        <div className="flex-1">
                          <Label className="text-xs text-amber-700 dark:text-amber-300">Categoría plan por defecto</Label>
                          <Select 
                            value={bulkPreview.default_categoria_plan || "salud_local"} 
                            onValueChange={(value) => setBulkPreview({...bulkPreview, default_categoria_plan: value})}
                          >
                            <SelectTrigger className="h-8 text-sm mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="salud_internacional">Salud Internacional</SelectItem>
                              <SelectItem value="salud_local">Salud Local</SelectItem>
                              <SelectItem value="vida">Vida</SelectItem>
                              <SelectItem value="accidentes">Accidentes</SelectItem>
                              <SelectItem value="mixto">Mixto</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </div>
                </ScrollArea>
              )}

              {/* Step 3: Results */}
              {bulkStep === 3 && bulkResult && (
                <div className="flex flex-col items-center py-8">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                    (bulkResult.aseguradoras_created > 0 || bulkResult.planes_created > 0) ? 'bg-green-100 dark:bg-green-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
                  }`}>
                    {(bulkResult.aseguradoras_created > 0 || bulkResult.planes_created > 0) ? (
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    ) : (
                      <AlertCircle className="w-10 h-10 text-amber-600" />
                    )}
                  </div>

                  <h3 className="text-xl font-semibold mb-2 text-slate-900 dark:text-white">
                    {(bulkResult.aseguradoras_created > 0 || bulkResult.planes_created > 0) ? "Importación Completada" : "Importación con Advertencias"}
                  </h3>
                  
                  <div className="flex gap-8 mb-6">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-green-600">{bulkResult.aseguradoras_created}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Aseguradoras</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-blue-600">{bulkResult.planes_created}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Planes</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-amber-600">{bulkResult.skipped}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Omitidos</p>
                    </div>
                  </div>

                  {bulkResult.errors?.length > 0 && (
                    <div className="w-full max-w-lg">
                      <h4 className="font-medium mb-2 text-slate-700 dark:text-slate-300">Detalles de errores:</h4>
                      <ScrollArea className="h-32 border rounded-lg p-3 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
                        {bulkResult.errors.map((err, idx) => (
                          <p key={idx} className="text-sm text-red-700 dark:text-red-300 mb-1">{err}</p>
                        ))}
                      </ScrollArea>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="border-t dark:border-slate-700 pt-4">
              {bulkStep === 1 && (
                <Button variant="outline" onClick={() => setShowBulkDialog(false)}>
                  Cancelar
                </Button>
              )}
              {bulkStep === 2 && (
                <>
                  <Button variant="outline" onClick={() => setBulkStep(1)}>
                    Volver
                  </Button>
                  <Button 
                    onClick={executeBulkImport}
                    disabled={bulkLoading || !Object.values(asegMapping).includes('name')}
                    className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                  >
                    {bulkLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    {bulkLoading ? "Importando..." : `Importar ${bulkPreview?.aseguradoras?.total_rows || 0} Aseguradoras${bulkPreview?.planes ? ` y ${bulkPreview.planes.total_rows || 0} Planes` : ''}`}
                  </Button>
                </>
              )}
              {bulkStep === 3 && (
                <Button onClick={() => setShowBulkDialog(false)} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
                  Cerrar
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
